import { Tooltip } from '@mui/material';
import { FunctionComponent, HTMLAttributes } from 'react';
import { HiExclamationCircle, HiExclamationTriangle } from 'react-icons/hi2';

export type FiguresAlertVariant = 'figures' | 'template-sync';

interface FiguresAlertProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: FiguresAlertVariant;
}

const TOOLTIP_STYLES: Record<
  FiguresAlertVariant,
  { tooltip: string; arrow: string; aria: string }
> = {
  figures: {
    tooltip:
      'bg-amber-50 text-amber-700 ring-amber-200 border border-gray-200 shadow-sm',
    arrow: 'text-amber-500',
    aria: 'Figures alert',
  },
  'template-sync': {
    tooltip:
      'bg-rose-50 text-rose-800 ring-rose-200 border border-gray-200 shadow-sm',
    arrow: 'text-rose-500',
    aria: 'KID template issue (missing or invalid blocks)',
  },
};

const FiguresAlert: FunctionComponent<FiguresAlertProps> = ({
  className,
  variant = 'figures',
  ...props
}) => {
  const styles = TOOLTIP_STYLES[variant];
  const title =
    variant === 'figures'
      ? 'Figures alert'
      : 'This KID is missing one or more active templates, or has invalid/inconsistent template blocks. Open the editor or template management to resolve.';

  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          className: styles.tooltip,
        },
        arrow: {
          className: styles.arrow,
        },
      }}
    >
      <span className={className} {...props}>
        {variant === 'figures' ? (
          <HiExclamationTriangle
            className="h-4 w-4 text-amber-500"
            aria-label={styles.aria}
          />
        ) : (
          <HiExclamationCircle
            className="h-4 w-4 text-rose-600"
            aria-label={styles.aria}
          />
        )}
      </span>
    </Tooltip>
  );
};

export default FiguresAlert;
